import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Badge, Button, Col, FormFeedback, FormGroup, Input, Label,
    Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner,
} from 'reactstrap';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import Flatpickr from 'react-flatpickr';
import { mergeOperationalDateCaps } from '../../../utils/flatpickrOperational';
import { ConditionSuggestionPills } from '../../../utils/deliveryConditionSuggestions';
import SignatureCanvas from 'react-signature-canvas';
import moment from 'moment';

import notify from '../../../custom/helpers/Notify';
import {
    locateBagRequest,
    receiveBagRequest,
    deliverBagRequest,
    forwardBagRequest,
    disposeBagRequest,
} from '../../../api/controller/BagActionController';
import { getArrivalFlightListRequest, getDepartureFlightListRequest } from '../../../api/controller/BagFinderController';

// ─── Shared helpers ───────────────────────────────────────────────────────────

const Field = ({ label, required, children, error }) => (
    <FormGroup className="mb-3">
        <Label className="fw-semibold mb-1" style={{ fontSize: '0.85rem' }}>
            {label}{required && <span className="text-danger ms-1">*</span>}
        </Label>
        {children}
        {error && <FormFeedback className="d-block">{error}</FormFeedback>}
    </FormGroup>
);

const fpOpts = mergeOperationalDateCaps({ dateFormat: 'Y-m-d', altInput: true, altFormat: 'F j, Y' });
const fpOptsDT = mergeOperationalDateCaps({ dateFormat: 'Y-m-d H:i', altInput: true, altFormat: 'F j, Y H:i', enableTime: true, time_24hr: true });

const selectStyles = (themeType) => themeType !== 'dark' ? {} : {
    control:     (b) => ({ ...b, backgroundColor: '#2a2f3d', borderColor: '#3d4354', color: '#e9ecef' }),
    menu:        (b) => ({ ...b, backgroundColor: '#2a2f3d' }),
    option:      (b, s) => ({ ...b, backgroundColor: s.isFocused ? '#3d4354' : '#2a2f3d', color: '#e9ecef' }),
    singleValue: (b) => ({ ...b, color: '#e9ecef' }),
    input:       (b) => ({ ...b, color: '#e9ecef' }),
};

const SummaryRow = ({ label, value, mono }) => value ? (
    <div className="d-flex gap-2 mb-1">
        <span style={{ minWidth: '110px', fontSize: '0.8rem', color: '#6c757d', fontWeight: 500 }}>{label}</span>
        <span style={{ fontSize: '0.85rem', fontFamily: mono ? 'monospace' : undefined, fontWeight: mono ? 600 : undefined }}>{value}</span>
    </div>
) : null;

// ─── Locate Modal (TRACING → LOCATED) ────────────────────────────────────────

export const LocateModal = ({ bag, onClose, onSuccess, themeType }) => {
    const [locationHistory, setLocationHistory] = useState(() =>
        (bag?.location_history || '').trim());
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const submit = async () => {
        const loc = locationHistory?.trim() || '';
        if (!loc) {
            setErrors({ LocationHistory: 'Arriving flight is required.' });
            return;
        }
        setErrors({});
        setLoading(true);
        const res = await locateBagRequest({ id: bag.id, LocationHistory: loc });
        setLoading(false);
        if (res?.Status === 'success') {
            notify('success', res.Message);
            onSuccess(res.Data);
        } else {
            notify('error', res?.Message || 'Something went wrong.');
        }
    };

    return (
        <Modal isOpen centered>
            <ModalHeader toggle={onClose}>
                <i className="mdi mdi-map-marker-check me-2 text-info" />
                Locate Bag
            </ModalHeader>
            <ModalBody>
                <div className="mb-4 p-3 rounded" style={{ background: themeType === 'dark' ? '#2a2f3d' : '#f8f9fa', border: '1px solid', borderColor: themeType === 'dark' ? '#3d4354' : '#e9ecef' }}>
                    <div className="fw-bold mb-1" style={{ fontFamily: 'monospace', fontSize: '1.1rem' }}>{bag?.tag_number}</div>
                    <div className="small" style={{ color: themeType === 'dark' ? '#adb5bd' : '#6c757d' }}>
                        {bag?.case_file?.type && <Badge color="primary" pill className="me-1">{bag.case_file.type}</Badge>}
                        {bag?.case_file?.reference}
                    </div>
                </div>

                <Field label="Arriving flight" required error={errors.LocationHistory}>
                    <Input
                        className={errors.LocationHistory ? 'is-invalid' : ''}
                        value={locationHistory}
                        onChange={e => { setLocationHistory(e.target.value); setErrors({}); }}
                        placeholder="e.g. FZ105"
                        autoFocus
                        onKeyDown={e => e.key === 'Enter' && submit()}
                    />
                </Field>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" outline onClick={onClose} disabled={loading}>Cancel</Button>
                <Button color="info" onClick={submit} disabled={loading} style={{ minWidth: '110px' }}>
                    {loading ? <Spinner size="sm" /> : <><i className="mdi mdi-map-marker-check me-1" />Mark Located</>}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

// ─── Receive Modal (LOCATED / TRACING / PROCESSING → RECEIVED) ───────────────

export const ReceiveModal = ({ bag, onClose, onSuccess, themeType }) => {
    const [form, setForm] = useState({
        ReceiveDate: moment().format('YYYY-MM-DD'),
        ReceivedWeight: '',
    });
    const [receivedFlight, setReceivedFlight] = useState(null);
    const [flightOptions, setFlightOptions] = useState([]);
    const [flightLoading, setFlightLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setErrors(p => ({ ...p, [k]: undefined })); };

    useEffect(() => {
        setForm({
            ReceiveDate: moment().format('YYYY-MM-DD'),
            ReceivedWeight: '',
        });
        setReceivedFlight(null);
        setFlightOptions([]);
        setErrors({});
    }, [bag?.id]);

    useEffect(() => {
        let cancelled = false;
        async function loadFlights() {
            if (!form.ReceiveDate) {
                setFlightOptions([]);
                setFlightLoading(false);
                return;
            }
            setFlightLoading(true);
            const res = await getArrivalFlightListRequest({ Date: form.ReceiveDate });
            if (cancelled) return;
            setFlightLoading(false);
            if (res && res.Status !== 'error' && Array.isArray(res.data)) {
                setFlightOptions(res.data.map(row => ({
                    label: row.name,
                    value: row.name,
                })));
            } else {
                setFlightOptions([]);
                if (res?.Status === 'error') {
                    notify('error', res.Message || 'Could not load arrival flights for this date.');
                }
            }
        }
        loadFlights();
        return () => { cancelled = true; };
    }, [form.ReceiveDate, bag?.id]);

    const flightString = receivedFlight ? String(receivedFlight.value ?? receivedFlight.label ?? '').trim() : '';

    const validate = () => {
        const e = {};
        if (!form.ReceiveDate)            e.ReceiveDate    = 'Receive date is required.';
        if (!flightString)                e.ReceivedFlight = 'Received flight is required.';
        if (!form.ReceivedWeight)         e.ReceivedWeight = 'Weight is required.';
        return e;
    };

    const submit = async () => {
        const e = validate();
        if (Object.keys(e).length) { setErrors(e); return; }
        setLoading(true);
        const res = await receiveBagRequest({
            id: bag.id,
            ReceiveDate:    form.ReceiveDate,
            ReceivedFlight: flightString,
            ReceivedWeight: form.ReceivedWeight,
        });
        setLoading(false);
        if (res?.Status === 'success') {
            notify('success', res.Message);
            onSuccess(res.Data);
        } else {
            notify('error', res?.Message || 'Something went wrong.');
        }
    };

    return (
        <Modal isOpen centered>
            <ModalHeader toggle={onClose}>
                <i className="mdi mdi-package-down me-2 text-warning" />
                Receive Bag
            </ModalHeader>
            <ModalBody>
                {bag?.bag_status?.name === 'TRACING' && (
                    <p className="small text-muted mb-3 mb-md-4" style={{ marginTop: '-0.25rem' }}>
                        Receiving directly from tracing skips locate; arriving flight (location history) is skipped.
                    </p>
                )}
                {bag?.bag_status?.name === 'PROCESSING' && (
                    <p className="small text-muted mb-3 mb-md-4" style={{ marginTop: '-0.25rem' }}>
                        DPR bags start in processing; receiving records physical receipt without a locate step (no location history required).
                    </p>
                )}
                <div className="mb-4 p-3 rounded" style={{ background: themeType === 'dark' ? '#2a2f3d' : '#f8f9fa', border: '1px solid', borderColor: themeType === 'dark' ? '#3d4354' : '#e9ecef' }}>
                    <div className="fw-bold mb-1" style={{ fontFamily: 'monospace', fontSize: '1.1rem' }}>{bag?.tag_number}</div>
                    <div className="small" style={{ color: themeType === 'dark' ? '#adb5bd' : '#6c757d' }}>
                        {bag?.case_file?.full_name && <span className="me-2">{bag.case_file.full_name}</span>}
                        {bag?.location_history && (
                            <span><i className="mdi mdi-airplane-landing me-1" />{bag.location_history}</span>
                        )}
                    </div>
                </div>

                <Field label="Receive Date" required error={errors.ReceiveDate}>
                    <Flatpickr
                        className={`form-control${errors.ReceiveDate ? ' is-invalid' : ''}`}
                        options={fpOpts}
                        value={form.ReceiveDate}
                        onChange={([d]) => {
                            const next = d ? moment(d).format('YYYY-MM-DD') : '';
                            setForm(p => ({ ...p, ReceiveDate: next }));
                            setReceivedFlight(null);
                            setErrors({});
                        }}
                        placeholder="Select date"
                    />
                </Field>

                <Row>
                    <Col md={7}>
                        <Field label="Received Flight" required error={errors.ReceivedFlight}>
                            <CreatableSelect
                                classNamePrefix="rs"
                                className={errors.ReceivedFlight ? 'is-invalid' : ''}
                                inputId="bag-receive-flight"
                                options={flightOptions}
                                value={receivedFlight}
                                onChange={v => { setReceivedFlight(v); setErrors(p => ({ ...p, ReceivedFlight: undefined })); }}
                                placeholder={form.ReceiveDate ? 'Select or type flight…' : 'Choose receive date first'}
                                isClearable
                                isDisabled={!form.ReceiveDate || loading}
                                isLoading={flightLoading}
                                styles={selectStyles(themeType)}
                                formatCreateLabel={i => `Use "${i}"`}
                            />
                        </Field>
                    </Col>
                    <Col md={5}>
                        <Field label="Weight (kg)" required error={errors.ReceivedWeight}>
                            <Input
                                type="number"
                                min="0"
                                step="0.01"
                                className={errors.ReceivedWeight ? 'is-invalid' : ''}
                                value={form.ReceivedWeight}
                                onChange={e => set('ReceivedWeight', e.target.value)}
                                placeholder="0.00"
                            />
                        </Field>
                    </Col>
                </Row>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" outline onClick={onClose} disabled={loading}>Cancel</Button>
                <Button color="warning" onClick={submit} disabled={loading} style={{ minWidth: '110px' }}>
                    {loading ? <Spinner size="sm" /> : <><i className="mdi mdi-package-down me-1" />Mark Received</>}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

// ─── Deliver Modal (RECEIVED → DELIVERED, 2-step) ────────────────────────────

const deliverNowFormatted = () => moment().format('YYYY-MM-DD HH:mm');

export const DeliverModal = ({ bag, onClose, onSuccess, themeType }) => {
    const [step, setStep] = useState(1); // 1 = info, 2 = signature
    const [form, setForm] = useState({
        DeliverDate:       deliverNowFormatted(),
        DeliveryName:      '',
        DeliveryContact:   '',
        DeliveryIdentity:  '',
        DeliveryCondition: '',
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const sigRef = useRef(null);

    useEffect(() => {
        setStep(1);
        setForm({
            DeliverDate:       deliverNowFormatted(),
            DeliveryName:      '',
            DeliveryContact:   '',
            DeliveryIdentity:  '',
            DeliveryCondition: '',
        });
        setErrors({});
        sigRef.current?.clear();
    }, [bag?.id]);

    const deliverFlatpickrOpts = useMemo(
        () => mergeOperationalDateCaps({
            dateFormat: 'Y-m-d H:i',
            altInput: true,
            altFormat: 'F j, Y H:i',
            enableTime: true,
            time_24hr: true,
            defaultDate: new Date(),
        }),
        [bag?.id],
    );

    const deliverDatePickerValue =
        form.DeliverDate && moment(form.DeliverDate, 'YYYY-MM-DD HH:mm', true).isValid()
            ? moment(form.DeliverDate, 'YYYY-MM-DD HH:mm').toDate()
            : new Date();

    const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setErrors(p => ({ ...p, [k]: undefined })); };

    const validateStep1 = () => {
        const e = {};
        if (!form.DeliverDate)       e.DeliverDate       = 'Delivery date & time is required.';
        if (!form.DeliveryName?.trim())      e.DeliveryName      = 'Recipient name is required.';
        if (!form.DeliveryIdentity?.trim())  e.DeliveryIdentity  = 'ID / identity is required.';
        if (!form.DeliveryCondition?.trim()) e.DeliveryCondition = 'Condition is required.';
        return e;
    };

    const goToSign = () => {
        const e = validateStep1();
        if (Object.keys(e).length) { setErrors(e); return; }
        setStep(2);
    };

    const submit = async () => {
        if (sigRef.current?.isEmpty()) {
            notify('error', 'Please obtain the customer signature before confirming.');
            return;
        }
        setLoading(true);
        const signatureData = sigRef.current.toDataURL('image/png');
        const res = await deliverBagRequest({
            id:                bag.id,
            DeliverDate:       form.DeliverDate,
            DeliveryName:      form.DeliveryName,
            DeliveryContact:   form.DeliveryContact || undefined,
            DeliveryIdentity:  form.DeliveryIdentity,
            DeliveryCondition: form.DeliveryCondition.trim(),
            DeliverySignature: signatureData,
        });
        setLoading(false);
        if (res?.Status === 'success') {
            notify('success', res.Message);
            onSuccess(res.Data);
        } else {
            notify('error', res?.Message || 'Something went wrong.');
        }
    };

    const sigPadBg = themeType === 'dark' ? '#1e2330' : '#fff';

    return (
        <Modal isOpen size="lg" centered>
            <ModalHeader toggle={onClose}>
                <i className="mdi mdi-hand-okay me-2 text-success" />
                Deliver Bag
                <span className="ms-3">
                    <Badge color={step === 1 ? 'success' : 'light'} pill className="me-1">1 Info</Badge>
                    <Badge color={step === 2 ? 'success' : 'light'} pill>2 Signature</Badge>
                </span>
            </ModalHeader>

            {step === 1 && (
                <>
                    <ModalBody>
                        <div className="mb-4 p-3 rounded" style={{ background: themeType === 'dark' ? '#2a2f3d' : '#f8f9fa', border: '1px solid', borderColor: themeType === 'dark' ? '#3d4354' : '#e9ecef' }}>
                            <div className="fw-bold mb-1" style={{ fontFamily: 'monospace', fontSize: '1.1rem' }}>{bag?.tag_number}</div>
                            <div className="small" style={{ color: themeType === 'dark' ? '#adb5bd' : '#6c757d' }}>
                                {bag?.case_file?.full_name && <span className="me-2"><i className="mdi mdi-account me-1" />{bag.case_file.full_name}</span>}
                                {bag?.case_file?.reference && <span>{bag.case_file.reference}</span>}
                            </div>
                        </div>

                        <Row>
                            <Col md={6}>
                                <Field label="Delivery Date & Time" required error={errors.DeliverDate}>
                                    <Flatpickr
                                        key={`bag-deliver-dt-${bag?.id ?? 'x'}`}
                                        className={`form-control${errors.DeliverDate ? ' is-invalid' : ''}`}
                                        options={deliverFlatpickrOpts}
                                        value={deliverDatePickerValue}
                                        onChange={([d]) => set('DeliverDate', d ? moment(d).format('YYYY-MM-DD HH:mm') : '')}
                                        placeholder="Select date & time"
                                    />
                                </Field>
                            </Col>
                            <Col md={6}>
                                <Field label="Identity / ID Number" required error={errors.DeliveryIdentity}>
                                    <Input
                                        className={errors.DeliveryIdentity ? 'is-invalid' : ''}
                                        value={form.DeliveryIdentity}
                                        onChange={e => set('DeliveryIdentity', e.target.value)}
                                        placeholder="Passport, ID card, etc."
                                    />
                                </Field>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Field label="Recipient Name" required error={errors.DeliveryName}>
                                    <Input
                                        className={errors.DeliveryName ? 'is-invalid' : ''}
                                        value={form.DeliveryName}
                                        onChange={e => set('DeliveryName', e.target.value)}
                                        placeholder="Full name"
                                    />
                                </Field>
                            </Col>
                            <Col md={6}>
                                <Field label="Contact">
                                    <Input
                                        value={form.DeliveryContact}
                                        onChange={e => set('DeliveryContact', e.target.value)}
                                        placeholder="Phone / email"
                                    />
                                </Field>
                            </Col>
                        </Row>

                        <Field label="Condition" required error={errors.DeliveryCondition}>
                            <ConditionSuggestionPills themeType={themeType} onPick={t => set('DeliveryCondition', t)} />
                            <Input
                                className={errors.DeliveryCondition ? 'is-invalid' : ''}
                                value={form.DeliveryCondition}
                                onChange={e => set('DeliveryCondition', e.target.value)}
                                placeholder="Describe bag condition"
                            />
                        </Field>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" outline onClick={onClose}>Cancel</Button>
                        <Button color="success" onClick={goToSign} style={{ minWidth: '160px' }}>
                            <i className="mdi mdi-draw me-1" />Next: Get Signature
                        </Button>
                    </ModalFooter>
                </>
            )}

            {step === 2 && (
                <>
                    <ModalBody>
                        {/* Delivery summary */}
                        <div className="mb-4 p-3 rounded" style={{ background: themeType === 'dark' ? '#2a2f3d' : '#f8f9fa', border: '1px solid', borderColor: themeType === 'dark' ? '#3d4354' : '#e9ecef' }}>
                            <div className="fw-bold mb-2" style={{ fontFamily: 'monospace', fontSize: '1.05rem' }}>
                                {bag?.tag_number}
                                {bag?.case_file?.reference && <span className="ms-2 fw-normal text-muted" style={{ fontSize: '0.85rem' }}>{bag.case_file.reference}</span>}
                            </div>
                            <SummaryRow label="Date & Time" value={form.DeliverDate ? moment(form.DeliverDate).format('MMM D, YYYY HH:mm') : ''} />
                            <SummaryRow label="Identity"   value={form.DeliveryIdentity} />
                            <SummaryRow label="Recipient"  value={form.DeliveryName} />
                            {form.DeliveryContact && <SummaryRow label="Contact" value={form.DeliveryContact} />}
                            <SummaryRow label="Condition"  value={form.DeliveryCondition?.trim()} />
                        </div>

                        {/* Signature pad */}
                        <div className="text-center">
                            <p className="fw-semibold mb-2" style={{ fontSize: '0.9rem', color: themeType === 'dark' ? '#e9ecef' : '#495057' }}>
                                Customer Signature
                            </p>
                            <div style={{ touchAction: 'none' }}>
                                <SignatureCanvas
                                    ref={sigRef}
                                    penColor={themeType === 'dark' ? '#e9ecef' : '#212529'}
                                    backgroundColor={sigPadBg}
                                    canvasProps={{ width: 300, height: 200, style: { border: `2px solid ${themeType === 'dark' ? '#3d4354' : '#dee2e6'}`, borderRadius: '8px' } }}
                                />
                            </div>
                            <Button color="link" size="sm" className="mt-1 text-muted" onClick={() => sigRef.current?.clear()}>
                                <i className="mdi mdi-refresh me-1" />Clear
                            </Button>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" outline onClick={() => { setForm(f => ({ ...f, DeliverDate: deliverNowFormatted() })); setStep(1); }} disabled={loading}>
                            <i className="mdi mdi-arrow-left me-1" />Back
                        </Button>
                        <Button color="success" onClick={submit} disabled={loading} style={{ minWidth: '160px' }}>
                            {loading ? <Spinner size="sm" /> : <><i className="mdi mdi-check-circle me-1" />Confirm Delivery</>}
                        </Button>
                    </ModalFooter>
                </>
            )}
        </Modal>
    );
};

// ─── Forward Modal (RECEIVED → FORWARDED) ────────────────────────────────────

export const ForwardModal = ({ bag, forwardTypeOptions, onClose, onSuccess, themeType }) => {
    const [form, setForm] = useState({ ForwardDate: moment().format('YYYY-MM-DD'), ForwardType: null, RushTagNumber: '' });
    const [forwardFlight, setForwardFlight] = useState(null);
    const [flightOptions, setFlightOptions] = useState([]);
    const [flightLoading, setFlightLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setErrors(p => ({ ...p, [k]: undefined })); };

    useEffect(() => {
        setForm({
            ForwardDate: moment().format('YYYY-MM-DD'),
            ForwardType: null,
            RushTagNumber: '',
        });
        setForwardFlight(null);
        setFlightOptions([]);
        setErrors({});
    }, [bag?.id]);

    useEffect(() => {
        let cancelled = false;
        async function loadFlights() {
            if (!form.ForwardDate) {
                setFlightOptions([]);
                setFlightLoading(false);
                return;
            }
            setFlightLoading(true);
            const res = await getDepartureFlightListRequest({ Date: form.ForwardDate });
            if (cancelled) return;
            setFlightLoading(false);
            if (res && res.Status !== 'error' && Array.isArray(res.data)) {
                setFlightOptions(res.data.map(row => ({
                    label: row.name,
                    value: row.name,
                })));
            } else {
                setFlightOptions([]);
                if (res?.Status === 'error') {
                    notify('error', res.Message || 'Could not load departure flights for this date.');
                }
            }
        }
        loadFlights();
        return () => { cancelled = true; };
    }, [form.ForwardDate, bag?.id]);

    const flightString = forwardFlight ? String(forwardFlight.value ?? forwardFlight.label ?? '').trim() : '';

    const validate = () => {
        const e = {};
        if (!form.ForwardDate)   e.ForwardDate   = 'Forward date is required.';
        if (!flightString)       e.ForwardFlight = 'Forward flight is required.';
        return e;
    };

    const submit = async () => {
        const e = validate();
        if (Object.keys(e).length) { setErrors(e); return; }
        setLoading(true);
        const res = await forwardBagRequest({
            id:            bag.id,
            ForwardDate:   form.ForwardDate,
            ForwardFlight: flightString,
            ForwardType:   form.ForwardType?.value || undefined,
            RushTagNumber: form.RushTagNumber || undefined,
        });
        setLoading(false);
        if (res?.Status === 'success') {
            notify('success', res.Message);
            onSuccess(res.Data);
        } else {
            notify('error', res?.Message || 'Something went wrong.');
        }
    };

    return (
        <Modal isOpen centered>
            <ModalHeader toggle={onClose}>
                <i className="mdi mdi-airplane-takeoff me-2 text-primary" />
                Forward Bag
            </ModalHeader>
            <ModalBody>
                <div className="mb-4 p-3 rounded" style={{ background: themeType === 'dark' ? '#2a2f3d' : '#f8f9fa', border: '1px solid', borderColor: themeType === 'dark' ? '#3d4354' : '#e9ecef' }}>
                    <div className="fw-bold mb-1" style={{ fontFamily: 'monospace', fontSize: '1.1rem' }}>{bag?.tag_number}</div>
                    <div className="small" style={{ color: themeType === 'dark' ? '#adb5bd' : '#6c757d' }}>
                        {bag?.case_file?.full_name && <span className="me-2"><i className="mdi mdi-account me-1" />{bag.case_file.full_name}</span>}
                        {bag?.receive_date && <span><i className="mdi mdi-calendar-check me-1" />Received {moment(bag.receive_date).format('MMM D, YYYY')}</span>}
                    </div>
                </div>

                <Row>
                    <Col md={6}>
                        <Field label="Forward Date" required error={errors.ForwardDate}>
                            <Flatpickr
                                className={`form-control${errors.ForwardDate ? ' is-invalid' : ''}`}
                                options={fpOpts}
                                value={form.ForwardDate}
                                onChange={([d]) => {
                                    const next = d ? moment(d).format('YYYY-MM-DD') : '';
                                    setForm(p => ({ ...p, ForwardDate: next }));
                                    setForwardFlight(null);
                                    setErrors({});
                                }}
                                placeholder="Select date"
                            />
                        </Field>
                    </Col>
                    <Col md={6}>
                        <Field label="Forward Flight" required error={errors.ForwardFlight}>
                            <CreatableSelect
                                classNamePrefix="rs"
                                className={errors.ForwardFlight ? 'is-invalid' : ''}
                                inputId="bag-forward-flight"
                                options={flightOptions}
                                value={forwardFlight}
                                onChange={v => { setForwardFlight(v); setErrors(p => ({ ...p, ForwardFlight: undefined })); }}
                                placeholder={form.ForwardDate ? 'Select or type flight…' : 'Choose forward date first'}
                                isClearable
                                isDisabled={!form.ForwardDate || loading}
                                isLoading={flightLoading}
                                styles={selectStyles(themeType)}
                                formatCreateLabel={i => `Use "${i}"`}
                            />
                        </Field>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Field label="Forward Type">
                            <Select
                                isClearable
                                options={forwardTypeOptions}
                                value={form.ForwardType}
                                onChange={v => set('ForwardType', v)}
                                placeholder="Select type…"
                                styles={selectStyles(themeType)}
                            />
                        </Field>
                    </Col>
                    <Col md={6}>
                        <Field label="Rush Tag Number">
                            <Input
                                value={form.RushTagNumber}
                                onChange={e => set('RushTagNumber', e.target.value)}
                                placeholder="Optional"
                            />
                        </Field>
                    </Col>
                </Row>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" outline onClick={onClose} disabled={loading}>Cancel</Button>
                <Button color="primary" onClick={submit} disabled={loading} style={{ minWidth: '130px' }}>
                    {loading ? <Spinner size="sm" /> : <><i className="mdi mdi-airplane-takeoff me-1" />Forward</>}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

// ─── Dispose Confirm (RECEIVED → DISPOSED) ────────────────────────────────────

export const DisposeModal = ({ bag, onClose, onSuccess, themeType }) => {
    const [loading, setLoading] = useState(false);

    const submit = async () => {
        setLoading(true);
        const res = await disposeBagRequest({ id: bag.id });
        setLoading(false);
        if (res?.Status === 'success') {
            notify('success', res.Message);
            onSuccess(res.Data);
        } else {
            notify('error', res?.Message || 'Something went wrong.');
        }
    };

    return (
        <Modal isOpen centered size="sm">
            <ModalHeader toggle={onClose}>
                <i className="mdi mdi-delete-forever me-2 text-danger" />
                Dispose Bag
            </ModalHeader>
            <ModalBody>
                <div className="text-center py-2">
                    <i className="mdi mdi-alert-circle text-warning" style={{ fontSize: '3rem' }} />
                    <p className="mt-3 mb-1 fw-semibold" style={{ color: themeType === 'dark' ? '#e9ecef' : '#212529' }}>
                        Mark this bag as disposed?
                    </p>
                    <p className="mb-0" style={{ fontFamily: 'monospace', fontSize: '1rem', fontWeight: 700 }}>{bag?.tag_number}</p>
                    <p className="mt-1 small" style={{ color: themeType === 'dark' ? '#adb5bd' : '#6c757d' }}>
                        This action will update the bag status to disposed.
                    </p>
                </div>
            </ModalBody>
            <ModalFooter className="justify-content-center gap-2">
                <Button color="secondary" outline onClick={onClose} disabled={loading}>Cancel</Button>
                <Button color="danger" onClick={submit} disabled={loading} style={{ minWidth: '110px' }}>
                    {loading ? <Spinner size="sm" /> : <><i className="mdi mdi-delete-forever me-1" />Dispose</>}
                </Button>
            </ModalFooter>
        </Modal>
    );
};
